/**
 * Shared team-level game analysis logic — Heuristic Engine v2.
 * Evaluates wins/opportunities using the CSV heuristic rules and
 * produces meta tags for practice focus (pill-style display).
 *
 * Used by:  CoachDashboard ForwardFocus · GameSummary PGA modal · Practice Plan auto-generator
 */

export function calcFourFactors(totals, oppTotals) {
  if (!totals) return null;
  const fga = totals.fga || 0;
  const fgm = totals.fgm || 0;
  const threepm = totals.three_pm || 0;
  const fta = totals.fta || 0;
  const to = totals.to || 0;
  const oreb = totals.oreb || 0;
  const oppDreb = oppTotals?.dreb || 0;

  const efg = fga > 0 ? ((fgm + 0.5 * threepm) / fga * 100) : 0;
  const tov = (fga + 0.44 * fta + to) > 0 ? (to / (fga + 0.44 * fta + to) * 100) : 0;
  const orb = (oreb + oppDreb) > 0 ? (oreb / (oreb + oppDreb) * 100) : 0;
  const ftr = fga > 0 ? (fta / fga * 100) : 0;

  return { efg, tov, orb, ftr };
}

/* ─── Helpers ─── */
function pct2(made, attempted) {
  return attempted > 0 ? (made / attempted * 100) : 0;
}

function rebPct(ourReb, oppReb) {
  return (ourReb + oppReb) > 0 ? (ourReb / (ourReb + oppReb) * 100) : 0;
}

/* ─── Main heuristic engine ─── */
export function generateTeamAnalysis(homeTotals, awayTotals, homeFourFactors, awayFourFactors, options = {}) {
  if (!homeFourFactors || !awayFourFactors || !homeTotals || !awayTotals) return null;

  const selfMode = options.selfComparison || false;
  const vsLabel = selfMode ? 'season avg' : '';

  const h = homeTotals;
  const a = awayTotals;
  const hf = homeFourFactors;
  const af = awayFourFactors;
  const v = (x) => x ?? 0;

  /* ── Derived stats ── */
  const ourEFG = hf.efg, oppEFG = af.efg;
  const ourTOV = hf.tov, oppTOV = af.tov;
  const ourORB = hf.orb, oppORB = af.orb;
  const ourFTR = hf.ftr, oppFTR = af.ftr;       // percentage (fta/fga*100)

  const our3PT = v(h.three_p_pct), opp3PT = v(a.three_p_pct);
  const ourFTPct = v(h.ft_pct);
  const ourPF = v(h.pf);

  // 2PT%
  const our2PM = v(h.fgm) - v(h.three_pm);
  const our2PA = v(h.fga) - v(h.three_pa);
  const opp2PM = v(a.fgm) - v(a.three_pm);
  const opp2PA = v(a.fga) - v(a.three_pa);
  const our2PT = pct2(our2PM, our2PA);
  const opp2PT = pct2(opp2PM, opp2PA);

  // REB%
  const ourREB = v(h.reb), oppREB = v(a.reb);
  const ourREBpct = rebPct(ourREB, oppREB);

  /* ── Apply heuristic rules from CSV ── */
  const wins = [];
  const opportunities = [];
  const tagSet = new Set();

  const addTags = (...tags) => { tags.filter(Boolean).forEach(t => tagSet.add(t)); };

  // --- Comparative + Absolute Rules ---

  // EFG% < opponent EFG%
  if (ourEFG < oppEFG) {
    opportunities.push(`Work on finding better shot opportunities on offense and contesting shots more consistently on defense (eFG ${ourEFG.toFixed(1)}% vs ${selfMode ? vsLabel + ' ' : ''}${oppEFG.toFixed(1)}%).`);
    addTags("Shooting Efficiency", "Shot Selection", "Rim Protection & Contesting", "Perimeter Defense");
  } else {
    wins.push(`Shooting efficiency – Your team ${selfMode ? 'is above' : 'had better shot execution than'} ${selfMode ? vsLabel : ''} (eFG ${ourEFG.toFixed(1)}% vs ${oppEFG.toFixed(1)}%).`);
  }

  // EFG% < 48%
  if (ourEFG < 48) {
    opportunities.push(`Focus on finding better shot opportunities on offense and making opponents work harder for theirs on defense (eFG ${ourEFG.toFixed(1)}%).`);
    addTags("Shot Selection");
  }

  // 3PT% < opponent 3PT%
  if (our3PT < opp3PT) {
    opportunities.push(`Focus on improving perimeter shooting on offense and contesting the opponent's perimeter shots on defense (3PT ${our3PT.toFixed(1)}% vs ${selfMode ? vsLabel + ' ' : ''}${opp3PT.toFixed(1)}%).`);
    addTags("Perimeter & Mid-Range Shooting", "Three-Point Shooting", "Perimeter Defense");
  } else if (our3PT > opp3PT) {
    wins.push(`Perimeter shooting – Your team ${selfMode ? 'is trending above' : 'out-shot them from three'} ${selfMode ? vsLabel : ''} (3PT ${our3PT.toFixed(1)}% vs ${opp3PT.toFixed(1)}%).`);
  }

  // 3PT% < 30%
  if (our3PT < 30) {
    opportunities.push(`Focus on perimeter shot mechanics and selection on offense, and making opponents work harder for open looks on defense (3PT ${our3PT.toFixed(1)}%).`);
    addTags("Three-Point Shooting", "Perimeter & Mid-Range Shooting");
  }

  // 2PT% < opponent 2PT%
  if (our2PT < opp2PT) {
    opportunities.push(`Focus on finishing strong at the rim on offense and protecting the paint more effectively on defense (2PT ${our2PT.toFixed(1)}% vs ${selfMode ? vsLabel + ' ' : ''}${opp2PT.toFixed(1)}%).`);
    addTags("Finishing at the Rim", "Rim Protection & Contesting");
  } else if (our2PT > opp2PT) {
    wins.push(`Finishing at the rim – Your team ${selfMode ? 'is trending above' : 'finished better inside'} ${selfMode ? vsLabel : ''} (2PT ${our2PT.toFixed(1)}% vs ${opp2PT.toFixed(1)}%).`);
  }

  // 2PT% < 48%
  if (our2PT < 48) {
    opportunities.push(`Work on footwork and touch around the basket on offense, and tightening up paint defense on the other end (2PT ${our2PT.toFixed(1)}%).`);
    addTags("Finishing at the Rim", "Perimeter & Mid-Range Shooting");
  }

  // TOV% > opponent TOV%
  if (ourTOV > oppTOV) {
    opportunities.push(`Focus on executing the offense more cleanly on offense and capitalizing on opponent mistakes on defense (TOV ${ourTOV.toFixed(1)}% vs ${selfMode ? vsLabel + ' ' : ''}${oppTOV.toFixed(1)}%).`);
    addTags("Ball Handling & Decision-Making", "Turnovers & Ball Security", "Steals & On-Ball Pressure");
  }

  // TOV% > 15%
  if (ourTOV > 15) {
    opportunities.push(`Work on ball security and decision-making on offense, and applying pressure to force turnovers on defense (TOV ${ourTOV.toFixed(1)}%).`);
    addTags("Turnovers & Ball Security");
  }

  // TOV% < opponent TOV% → explicit WIN from CSV
  if (ourTOV < oppTOV) {
    wins.push(`Your team is ${selfMode ? 'trending below' : 'creating turnovers with active on-ball pressure'} ${selfMode ? vsLabel + ' on turnovers' : ''} \u2014 keep it up and turn those into easy buckets (TOV ${ourTOV.toFixed(1)}% vs ${oppTOV.toFixed(1)}%).`);
    addTags("Steals & On-Ball Pressure");
  }

  // ORB% < opponent ORB%
  if (ourORB < oppORB) {
    opportunities.push(`Focus on crashing the offensive glass harder and establishing better rebounding position on defense (ORB ${ourORB.toFixed(1)}% vs ${selfMode ? vsLabel + ' ' : ''}${oppORB.toFixed(1)}%).`);
    addTags("Offensive Rebounding", "Defensive Rebounding");
  } else if (ourORB > oppORB) {
    wins.push(`Offensive rebounding – Your team ${selfMode ? 'is trending above' : 'created second chances'} ${selfMode ? vsLabel : ''} (ORB ${ourORB.toFixed(1)}% vs ${oppORB.toFixed(1)}%).`);
  }

  // ORB% < 20%
  if (ourORB < 20) {
    opportunities.push(`Work on effort and positioning on the offensive boards, and boxing out more consistently on defense (ORB ${ourORB.toFixed(1)}%).`);
    addTags("Offensive Rebounding");
  }

  // REB% < opponent REB%
  if (ourREBpct < 50 && ourREB < oppREB) {
    opportunities.push(`Focus on winning the rebounding battle on both ends \u2014 positioning and effort make the difference (${ourREB} vs ${oppREB} rebounds).`);
    addTags("Defensive Rebounding", "Offensive Rebounding");
  } else if (ourREB > oppREB) {
    wins.push(`Rebounding – Your team controlled the glass (${ourREB} vs ${oppREB} rebounds).`);
  }

  // REB% < 50%
  if (ourREBpct < 50) {
    opportunities.push(`Focus on winning the rebounding battle on both ends \u2014 positioning and effort make the difference (REB ${ourREBpct.toFixed(1)}%).`);
    addTags("Defensive Rebounding");
  }

  // FTR < opponent FTR
  if (ourFTR < oppFTR) {
    opportunities.push(`Work on attacking the basket and drawing contact on offense, and contesting shots without fouling on defense (FTR ${(ourFTR / 100).toFixed(2)} vs ${selfMode ? vsLabel + ' ' : ''}${(oppFTR / 100).toFixed(2)}).`);
    addTags("Attacking the Rim", "Drawing Contact & Fouls");
  } else if (ourFTR > oppFTR) {
    wins.push(`Attacking the basket – Your team ${selfMode ? 'is trending above' : 'drew more fouls'} ${selfMode ? vsLabel : ''} (FTR ${(ourFTR / 100).toFixed(2)} vs ${(oppFTR / 100).toFixed(2)}).`);
  }

  // FTR < 0.20
  if (ourFTR < 20) {
    opportunities.push(`Focus on getting to the line more on offense and limiting opponent free throw opportunities on defense (FTR ${(ourFTR / 100).toFixed(2)}).`);
    addTags("Attacking the Rim", "Drawing Contact & Fouls");
  }

  // FT% < 75%
  if (ourFTPct < 75) {
    opportunities.push(`Focus on converting at the free throw line on offense and limiting opponent free throw opportunities on defense (FT ${ourFTPct.toFixed(1)}%).`);
    addTags("Free-Throw Consistency");
  } else {
    wins.push(`Free throws – Your team capitalised at the line (FT ${ourFTPct.toFixed(1)}%).`);
  }

  // FOULS > 12
  if (ourPF > 12) {
    opportunities.push(`Limit the foul count \u2014 let the defense work without sending opponents to the line (${ourPF} fouls).`);
    addTags("Foul Discipline", "Defensive Discipline", "Drawing Contact & Fouls");
  }

  return {
    wins: wins.slice(0, 3),
    opportunities: opportunities.slice(0, 3),
    practiceFocus: [...tagSet],
    selfComparison: selfMode,
  };
}
